import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { WifiOff, ShieldCheck, Globe2, ArrowRight, Store, UserCircle2 } from "lucide-react";
import { storage } from "@/lib/storage";
import { authApi } from "@/lib/auth";
import { GoogleButton } from "@/components/GoogleButton";

export default function Landing() {
  const navigate = useNavigate();
  const [hasPin, setHasPin] = useState(false);
  const user = storage.getUser();

  useEffect(() => {
    setHasPin(storage.hasPin());
  }, []);

  const start = (role) => {
    if (!storage.getUser()) {
      authApi.login(role);
    } else if (!storage.hasPin()) {
      sessionStorage.setItem("opg_pending_role", role);
      navigate("/pin-setup");
    } else if (storage.isUnlocked()) {
      // Skip PIN - already unlocked within 5 min
      navigate(role === "merchant" ? "/merchant" : "/customer");
    } else {
      navigate(`/unlock?role=${role}`);
    }
  };

  return (
    <div className="page-shell" data-testid="landing-page">
      <div className="flex items-center justify-between mb-10 fade-slide-up">
        <div className="flex items-center gap-3">
          <Logo size={44} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#F7E6B4]/55 font-medium">
              Offline Pay
            </div>
            <div className="text-base font-semibold gold-text" data-testid="brand-name">
              AURUM GATEWAY
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border gold-border bg-[rgba(230,180,85,0.06)]">
          <WifiOff size={12} className="text-[#E6B455]" />
          <span className="text-[10px] uppercase tracking-wider text-[#E6B455] font-medium">
            No Internet Needed
          </span>
        </div>
      </div>

      <div className="fade-slide-up" style={{ animationDelay: "0.08s" }}>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
          <span className="text-[#F7E6B4]">Payments that</span>
          <br />
          <span className="gold-text">never go offline.</span>
        </h1>
        <p className="text-base text-[#F7E6B4]/65 max-w-md leading-relaxed">
          Accept and send payments without internet. Secured by 4-digit PIN. Multi-currency,
          QR + NFC + Manual + Cash record. Everything stays on your device.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 my-10 fade-slide-up" style={{ animationDelay: "0.16s" }}>
        {[
          { icon: ShieldCheck, label: "PIN Secured" },
          { icon: Globe2, label: "10 Currencies" },
          { icon: WifiOff, label: "100% Offline" },
        ].map((f, i) => (
          <div key={i} className="stat-card text-center" data-testid={`feature-${i}`}>
            <f.icon size={20} className="text-[#E6B455] mx-auto mb-2" />
            <div className="text-xs text-[#F7E6B4]/70 font-medium">{f.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3 fade-slide-up" style={{ animationDelay: "0.24s" }}>
        {user ? (
          <div className="flex items-center gap-3 px-1 pb-1" data-testid="signed-in-chip">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border gold-border" />}
            <div className="text-xs text-[#F7E6B4]/65">
              Signed in as <span className="text-[#E6B455] font-semibold" data-testid="signed-in-email">{user.email}</span>
            </div>
          </div>
        ) : (
          <>
            <GoogleButton />
            <p className="text-center text-[11px] text-[#F7E6B4]/45 pb-2">
              One-time sign-in. After that, your PIN works fully offline.
            </p>
          </>
        )}
        <button
          onClick={() => start("merchant")}
          className="w-full card-glass gold-border rounded-2xl p-5 flex items-center justify-between hover:border-[rgba(230,180,85,0.4)] transition group"
          data-testid="role-merchant-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
              <Store size={22} className="text-[#E6B455]" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-[#F7E6B4]">I'm a Merchant</div>
              <div className="text-xs text-[#F7E6B4]/55 mt-0.5">Receive payments offline</div>
            </div>
          </div>
          <ArrowRight size={18} className="text-[#E6B455] group-hover:translate-x-1 transition" />
        </button>

        <button
          onClick={() => start("customer")}
          className="w-full card-glass gold-border rounded-2xl p-5 flex items-center justify-between hover:border-[rgba(230,180,85,0.4)] transition group"
          data-testid="role-customer-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
              <UserCircle2 size={22} className="text-[#E6B455]" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-[#F7E6B4]">I'm a Customer</div>
              <div className="text-xs text-[#F7E6B4]/55 mt-0.5">Pay a merchant offline</div>
            </div>
          </div>
          <ArrowRight size={18} className="text-[#E6B455] group-hover:translate-x-1 transition" />
        </button>
      </div>

      <p className="text-center text-[11px] text-[#F7E6B4]/35 mt-10">
        Offline demo gateway · Data stored locally on this device
      </p>
    </div>
  );
}
